// src/hooks/useActionPlans.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createActionPlan,
  createActionPlanEvidence,
  createActionPlanExecutionItemAuditLog,
  listActionPlanExecutionBoard,
  listActionPlanExecutionItemAuditLogs,
  listActionPlanExecutionResponsibleUsers,
  createActionPlanItem,
  getActionPlan,
  listActionPlanAuditLogs,
  listActionPlanEvidences,
  listActionPlans,
  setActionPlanItemStatus,
  setActionPlanStatus,
  updateActionPlan,
  updateActionPlanItem,
} from "@/services/actionPlans";
import type {
  ActionPlanCreateIn,
  ActionPlanEvidenceCreateIn,
  ActionPlanExecutionAuditLogCreateIn,
  ActionPlanItemCreateIn,
  ActionPlanItemStatus,
  ActionPlanItemUpdateIn,
  ActionPlanStatus,
  ActionPlanUpdateIn,
} from "@/types/actionPlans";

export const ACTION_PLANS_QK = (params: {
  status?: string;
  risk_inventory_id?: string;
  risk_inventory_item_id?: string;
}) => ["action_plans", params] as const;



export function useActionPlanExecutionBoard(params: {
  inventory_id?: string;
  inventory_item_id?: string;
  risk_catalog_id?: string;
  action_plan_id?: string;
  status?: string;
  priority?: string;
  responsible_user_id?: string;
  item_origin?: string;
  deadline_state?: string;
  q?: string;
}) {
  return useQuery({
    queryKey: ["action_plan_execution_board", params],
    queryFn: () => listActionPlanExecutionBoard(params),
    staleTime: 10_000,
  });
}


export function useActionPlanExecutionItemAuditLogs(itemId: string | undefined) {
  return useQuery({
    queryKey: ["action_plan_execution_item_audit_logs", itemId],
    queryFn: () => listActionPlanExecutionItemAuditLogs(itemId as string),
    enabled: !!itemId,
    staleTime: 5_000,
  });
}

export function useCreateActionPlanExecutionItemAuditLog() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      itemId,
      body,
    }: {
      itemId: string;
      body: ActionPlanExecutionAuditLogCreateIn;
    }) => createActionPlanExecutionItemAuditLog(itemId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["action_plan_execution_item_audit_logs", variables.itemId],
      });
    },
  });
}

export function useActionPlanExecutionResponsibleUsers() {
  return useQuery({
    queryKey: ["action_plan_execution_responsible_users"],
    queryFn: () => listActionPlanExecutionResponsibleUsers(),
    staleTime: 60_000,
  });
}
export function useActionPlans(params: {
  status?: string;
  risk_inventory_id?: string;
  risk_inventory_item_id?: string;
}) {
  return useQuery({
    queryKey: ACTION_PLANS_QK(params),
    queryFn: () => listActionPlans(params),
    staleTime: 20_000,
  });
}

export function useActionPlanDetail(id: string | undefined) {
  return useQuery({
    queryKey: ["action_plan_detail", id],
    queryFn: () => getActionPlan(id as string),
    enabled: !!id,
    staleTime: 20_000,
  });
}

export function useActionPlanAuditLogs(id: string | undefined) {
  return useQuery({
    queryKey: ["action_plan_audit_logs", id],
    queryFn: () => listActionPlanAuditLogs(id as string),
    enabled: !!id,
    staleTime: 5_000,
  });
}

export function useActionPlanEvidences(itemId: string | undefined) {
  return useQuery({
    queryKey: ["action_plan_evidences", itemId],
    queryFn: () => listActionPlanEvidences(itemId as string),
    enabled: !!itemId,
    staleTime: 20_000,
  });
}

export function useCreateActionPlan() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: ActionPlanCreateIn) => createActionPlan(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["action_plans"] });
    },
  });
}

export function useUpdateActionPlan() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      actionPlanId,
      body,
    }: {
      actionPlanId: string;
      body: ActionPlanUpdateIn;
    }) => updateActionPlan(actionPlanId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["action_plans"] });
      qc.invalidateQueries({
        queryKey: ["action_plan_detail", variables.actionPlanId],
      });
    },
  });
}

export function useSetActionPlanStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      actionPlanId,
      status,
    }: {
      actionPlanId: string;
      status: ActionPlanStatus;
    }) => setActionPlanStatus(actionPlanId, { status }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["action_plans"] });
      qc.invalidateQueries({
        queryKey: ["action_plan_detail", variables.actionPlanId],
      });
      qc.invalidateQueries({
        queryKey: ["action_plan_audit_logs", variables.actionPlanId],
      });
    },
  });
}

export function useCreateActionPlanItem() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      actionPlanId,
      body,
    }: {
      actionPlanId: string;
      body: ActionPlanItemCreateIn;
    }) => createActionPlanItem(actionPlanId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["action_plan_detail", variables.actionPlanId],
      });
      qc.invalidateQueries({ queryKey: ["action_plans"] });
      qc.invalidateQueries({
        queryKey: ["action_plan_audit_logs", variables.actionPlanId],
      });
    },
  });
}

export function useUpdateActionPlanItem() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      itemId,
      actionPlanId,
      body,
    }: {
      itemId: string;
      actionPlanId: string;
      body: ActionPlanItemUpdateIn;
    }) => updateActionPlanItem(itemId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["action_plan_detail", variables.actionPlanId],
      });
      qc.invalidateQueries({
        queryKey: ["action_plan_audit_logs", variables.actionPlanId],
      });
    },
  });
}

export function useSetActionPlanItemStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      itemId,
      actionPlanId,
      status,
    }: {
      itemId: string;
      actionPlanId: string;
      status: ActionPlanItemStatus;
    }) => setActionPlanItemStatus(itemId, { status }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["action_plan_detail", variables.actionPlanId],
      });
      qc.invalidateQueries({
        queryKey: ["action_plan_audit_logs", variables.actionPlanId],
      });
    },
  });
}

export function useCreateActionPlanEvidence() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      itemId,
      body,
    }: {
      itemId: string;
      body: ActionPlanEvidenceCreateIn;
    }) => createActionPlanEvidence(itemId, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ["action_plan_evidences", variables.itemId],
      });
    },
  });
}
